import { zodResolver } from '@hookform/resolvers/zod';
import { type DeleteAccountDto, DeleteAccountSchema } from '@nestjs-boilerplate/types';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { clearAuthState } from '@/stores/auth.store';
import { useDeleteAccount } from '../api/settings';

interface DeleteAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
}

export function DeleteAccountModal({ open, onOpenChange, userEmail }: DeleteAccountModalProps) {
  const { mutate: deleteAccount, isPending } = useDeleteAccount();

  const form = useForm<DeleteAccountDto>({
    resolver: zodResolver(DeleteAccountSchema),
    defaultValues: {
      confirmEmail: '',
      password: '',
    },
  });

  function handleSubmit(data: DeleteAccountDto) {
    deleteAccount(data, {
      onSuccess: () => {
        // Clear auth state and redirect to login
        clearAuthState();
        window.location.href = '/login';
      },
    });
  }

  function handleClose() {
    form.reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-red-600">Delete Account</DialogTitle>
          <DialogDescription>
            Your account will be scheduled for deletion after a 30-day grace period. During this
            time, you can contact support to restore your account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="confirmEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm your email address</FormLabel>
                  <FormControl>
                    <Input placeholder={userEmail} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Enter your password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending ? 'Deleting...' : 'Delete Account'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
